import type { IUseDateInput } from '$/components/elements/Inputs/hooks/useDateInput';
import type { IInputQuestion } from '../../Questions/_question';
import type { ReactDatePickerProps } from 'react-datepicker';
import type { UseFormReturn } from 'react-hook-form';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import de from 'date-fns/locale/de';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';

import { H6Typography } from '../../Typography';
import TextInput from '../TextInput';

import DatePickerWrapper from './DatePickerWrapper';
import IndicatorWrapper from './IndicatorWrapper';

import theme from '$/assets/theme';
import useDateInput from '$/components/elements/Inputs/hooks/useDateInput';
import { DateTypes } from '$/types/enums/DateTypes';
import dateFormatToGermanPlaceholder from '$/utils/transforms/dateToGermanPlaceholder';

interface IIndicatorProps {
  show: boolean;
  isError: boolean;
  errorTitle?: string;
  errorMessage?: string;
  successTitle?: string;
  successMessage?: string;
}
export interface IDateInputProps extends Omit<IUseDateInput, 'dateFormat'> {
  name: string;
  onBlur?: () => void;
  iconPosition?: 'end' | 'start';
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  error?: boolean;
  errorMessage?: IInputQuestion['errorMessage'];
  disabled?: boolean;
  monthYearPickerProps?: Omit<ReactDatePickerProps, 'onChange'>;
  setError?: UseFormReturn<any>['setError'];
  indicatorProps?: IIndicatorProps;
}

const dateFormat = DateTypes.MONTH_YEAR_DOT;
const placeholder = dateFormatToGermanPlaceholder(dateFormat);

const MonthYearInput = React.forwardRef(
  (
    {
      value,
      onChange,
      iconPosition,
      error,
      monthYearPickerProps,
      indicatorProps,
      ...props
    }: IDateInputProps,
    _
  ) => {
    const {
      dialogRef,
      datePickerValue,
      handleDatePickerOnChange,
      handleTextInputOnChange,
      textInputValue,
      openModal,
      isModalOpen,
      closeModal,
    } = useDateInput({ value, onChange, dateFormat, ...props });

    const [datePickerSelected, setDatePickerSelected] = useState(false);
    return (
      <div ref={dialogRef}>
        <DatePickerWrapper>
          <DatePicker
            locale={de}
            selected={datePickerValue}
            onChange={(e) => {
              setDatePickerSelected(true);
              handleDatePickerOnChange()(e);
            }}
            showMonthYearPicker
            onCalendarClose={closeModal}
            open={isModalOpen}
            showTimeInput={false}
            {...(monthYearPickerProps ?? {})}
          />
        </DatePickerWrapper>
        <TextInput
          {...props}
          placeholder={placeholder}
          value={textInputValue}
          onInput={handleTextInputOnChange}
          icon={
            <FontAwesomeIcon
              name={props.name}
              icon={faCalendar}
              onClick={openModal}
              style={{ cursor: 'pointer' }}
            />
          }
          iconPosition={iconPosition}
          ref={dialogRef}
          errorMessage={props.errorMessage ?? 'Bitte angeben'}
          error={error}
          onBlur={props.onBlur}
          containerProps={{
            style: {
              borderColor: error ? theme.colors.functionalRed : undefined,
            },
          }}
          inputProps={{
            autoComplete: 'off',
          }}
          datePickerSelected={datePickerSelected}
          maskedInput
          hideErrorIcon
        />
        {indicatorProps?.show && (
          <IndicatorWrapper isError={indicatorProps.isError}>
            {indicatorProps.isError ? (
              <>
                <H6Typography bold style={{ color: '#FA3435' }}>
                  {indicatorProps.errorTitle}
                </H6Typography>
                <H6Typography>{indicatorProps.errorMessage}</H6Typography>
              </>
            ) : (
              <>
                <H6Typography bold style={{ color: 'green' }}>
                  {indicatorProps.successTitle}
                </H6Typography>
                <H6Typography>{indicatorProps.successMessage}</H6Typography>
              </>
            )}
          </IndicatorWrapper>
        )}
      </div>
    );
  }
);

MonthYearInput.defaultProps = {
  error: false,
  iconPosition: 'end',
};

export default MonthYearInput;
